import React, { useState, useContext } from 'react';
import { Table } from '@shared/components/ui/Table';
import { Button } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ModificationModal } from './ModificationModal';
import { DeleteModal } from './DeleteModal';
import BasicsContext from '@shared/context/BasicsContext';
import { ErrorAPI } from '@shared/components/ui/ErrorAPI';
import { useStyles } from '../styles/TableJournal.styles';
import { useActivitiesUser, useAllUsersActivities } from '../hooks/useActivityMutations';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const TableJournalUser = (props) => {
  const classes = useStyles();
  const { userId, date } = props;
  const basics = useContext(BasicsContext);
  const timezones = basics ? basics.timezones : null;
  const activityTypes = basics ? basics.board_activity_types : null;
  const [selectedActivity, setSelectedActivity] = useState();
  const [openModificationModal, setOpenModificationModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // Use TanStack Query to fetch activities
  const { data, isError, isLoading } = useActivitiesUser({ userId, date, activityTypes });

  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: {
        filter: false,
        sort: true,
        display: false,
      },
    },
    {
      name: 'started_at',
      label: 'Date',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => dayjs(value).format('DD/MM/YYYY'),
      },
    },
    {
      name: 'ended_at',
      label: 'Heure de fin',
      options: {
        filter: false,
        sort: false,
        display: 'excluded',
      },
    },
    {
      name: 'started_at',
      label: 'Heure de début',
      options: {
        filter: false,
        sort: true,

        customBodyRender: (value, tableMeta, updateValue) => dayjs(value).format('HH:mm'),
      },
    },

    {
      name: 'duration',
      label: 'Durée',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'manual',
      label: 'Type de saisie',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => (value ? (value = 'Manuelle') : (value = 'Directe')),
      },
    },
    {
      name: 'activity_type_id',
      options: {
        filter: false,
        sort: false,
        display: 'excluded',
      },
    },
    {
      name: 'special_activity_name',

      options: {
        filter: false,
        sort: false,
        display: 'excluded',
      },
    },
    {
      name: 'activity_type',
      label: 'Intitulé',
      options: {
        filter: true,
        sort: true,
      },
    },

    {
      name: 'timezone_id',
      label: 'Zone de travail',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => timezones.find((x) => x.id === value).display_name,
      },
    },
    {
      name: 'reinforcement',
      label: 'Renfort',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => (value ? <CheckIcon /> : <ClearIcon />),
        filterOptions: {
          renderValue: (v) => (v ? 'oui' : 'non'),
        },
      },
    },
    {
      name: 'modify',
      label: 'Modifier',
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={tableMeta.rowData[4] === 'en cours'}
              className={classes.button}
              onClick={() => handleModificationModal(tableMeta)}
            >
              <EditIcon />
            </Button>
          );
        },
      },
    },
    {
      name: 'delete',
      label: 'Supprimer',
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={tableMeta.rowData[4] === 'en cours'}
              className={classes.button}
              onClick={() => {
                handleDeleteModal(tableMeta);
              }}
            >
              <DeleteForeverIcon />
            </Button>
          );
        },
      },
    },
  ];

  const handleModificationModal = (tableMeta) => {
    const activityId = tableMeta.rowData[0];
    const activity = data?.find((item) => item.id === activityId);
    setSelectedActivity(activity);
    setOpenModificationModal(true);
  };

  const handleCloseModificationModal = (event) => {
    setOpenModificationModal(false);
    setSelectedActivity();
  };

  const handleDeleteModal = (tableMeta) => {
    const activityId = tableMeta.rowData[0];
    const activity = data?.find((item) => item.id === activityId);
    setSelectedActivity(activity);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = (event) => {
    setOpenDeleteModal(false);
  };

  return (
    <>
      <ErrorAPI
        errorAPI={isError}
        setErrorAPI={() => {}}
        message={
          'Un problème est survenu lors du chargement des données, veuillez recharger la page ou réessayer plus tard.'
        }
      />

      <Table
        title={"Journal d'activité"}
        data={data}
        columns={columns}
        loading={isLoading}
      />

      {selectedActivity && (
        <ModificationModal
          openModal={openModificationModal}
          handleCloseModal={handleCloseModificationModal}
          activity={selectedActivity}
        />
      )}

      <DeleteModal
        openModal={openDeleteModal}
        handleCloseModal={handleCloseDeleteModal}
        activity={selectedActivity}
      />
    </>
  );
};

export const TableJournalGlobal = (props) => {
  const { date } = props;
  const basics = useContext(BasicsContext);
  const timezones = basics ? basics.timezones : null;
  const activityTypes = basics ? basics.board_activity_types : null;

  // Use TanStack Query to fetch activities for all users with user names
  const { data, isError, isLoading } = useAllUsersActivities({ date, activityTypes });

  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: {
        filter: false,
        sort: true,
        display: false,
      },
    },
    {
      name: 'user_name',
      label: 'Utilisateur',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'started_at',
      label: 'Date',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => dayjs(value).format('DD/MM/YYYY'),
      },
    },
    {
      name: 'started_at',
      label: 'Heure de début',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => dayjs(value).format('HH:mm'),
      },
    },
    {
      name: 'duration',
      label: 'Durée',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'manual',
      label: 'Type de saisie',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => (value ? (value = 'Manuelle') : (value = 'Directe')),
      },
    },
    {
      name: 'activity_type',
      label: 'Intitulé',
      options: {
        filter: true,
        sort: true,
      },
    },

    {
      name: 'timezone_id',
      label: 'Zone de travail',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => timezones.find((x) => x.id === value).display_name,
      },
    },
    {
      name: 'reinforcement',
      label: 'Renfort',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => (value ? <CheckIcon /> : <ClearIcon />),
        filterOptions: {
          renderValue: (v) => (v ? 'oui' : 'non'),
        },
      },
    },
  ];

  return (
    <>
      <ErrorAPI
        errorAPI={isError}
        setErrorAPI={() => {}}
        message={
          'Un problème est survenu lors du chargement des données, veuillez recharger la page ou réessayer plus tard.'
        }
      />
      <Table
        title={'Journal de tous les utilisateurs'}
        data={data}
        columns={columns}
        loading={isLoading}
      />
    </>
  );
};
